// Public API: suggestFunctionsInScope(scope, snapshot, filter?) → Routine[].
// Pure function. Returns the routines callable at the cursor, filtered by
// scope.currentSchema + engine-specific fallbacks:
//   postgres : currentSchema (or 'public' default) + 'pg_catalog'
//   mssql    : currentSchema (or 'dbo' default) + 'sys' + 'dbo'
//   mysql    : currentSchema (or currentDatabase) + 'mysql'
//   oracle   : currentSchema + 'SYS' + 'PUBLIC'  (PUBLIC synonym fallback)
//   sqlite   : no schema filter — built-ins are schemaless, user functions
//              registered against the attached-db may carry a schema, but
//              the sqlite catalog surface for routines doesn't.
//
// Signature follows the established suggest-* shape (snapshot threaded as the
// second positional arg — Scope carries no SchemaSnapshot reference).
//
// Overloads: snapshot.routines is a ReadonlyMap<foldedName, Routine[]>. Each
// Routine already carries its own `overloads` field (the producer aggregates
// per-name overload sets). This function emits each Routine from the map as
// one output entry, deduped on (foldedSchema, foldedName) because the same
// routine-qualified-name shouldn't surface twice; distinct schemas with the
// same function name (e.g. pg_catalog.now vs public.now) stay as separate
// entries.
//
// Filter: case-insensitive prefix match. Oracle folds to UPPERCASE before
// compare (matching the Oracle CI-by-uppercase convention from
// scope/identifiers.ts); every other engine folds to lowercase.
//
// Ordering: current-schema bucket first (current schema + schemaless), then
// other buckets; within each bucket sorted by (foldedSchema, foldedName).
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Suggestion — Functions
// in Scope (migrates FunctionSignatureHelp).

import type { Routine, SchemaSnapshot, Scope } from '../types';

export function suggestFunctionsInScope(
  scope: Scope,
  snapshot: SchemaSnapshot | undefined,
  filter?: string,
): Routine[] {
  if (!snapshot) return [];

  const engine = snapshot.engineId;
  const allowedSchemas = buildAllowedSchemas(engine, scope);
  const filterFolded = filter ? foldForMatch(filter, engine) : undefined;

  const currentFolded = scope.currentSchema ? foldForMatch(scope.currentSchema, engine) : undefined;

  const out: Routine[] = [];
  const seen = new Set<string>();
  for (const routines of snapshot.routines.values()) {
    for (const r of routines) {
      const qn = r.signature.qualified;
      if (!schemaInScope(qn.foldedSchema, allowedSchemas)) continue;
      if (filterFolded && !foldForMatch(qn.name, engine).startsWith(filterFolded)) continue;
      const key = `${qn.foldedSchema ?? ''}|${qn.foldedName}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
    }
  }

  out.sort((a, b) => compareRoutines(a, b, currentFolded));
  return out;
}

// Build the set of folded schema names whose routines should surface.
// Returns undefined when the engine has no schema concept for routines
// (SQLite built-ins) — schemaInScope() treats undefined as "any schema OK".
function buildAllowedSchemas(engine: string, scope: Scope): Set<string> | undefined {
  if (engine === 'sqlite') return undefined;

  const schemas = new Set<string>();
  if (scope.currentSchema) schemas.add(foldForMatch(scope.currentSchema, engine));

  switch (engine) {
    case 'postgres': {
      if (schemas.size === 0) schemas.add('public');
      schemas.add('pg_catalog');
      break;
    }
    case 'mssql': {
      if (schemas.size === 0) schemas.add('dbo');
      schemas.add('sys');
      schemas.add('dbo');
      break;
    }
    case 'mysql': {
      if (scope.currentDatabase) schemas.add(foldForMatch(scope.currentDatabase, engine));
      schemas.add('mysql');
      break;
    }
    case 'oracle': {
      schemas.add('SYS');
      schemas.add('PUBLIC');
      break;
    }
    default: {
      // Unknown engine: be permissive. Callers with custom engineIds
      // (e.g. test harnesses) get full routine list.
      return undefined;
    }
  }
  return schemas;
}

function schemaInScope(
  foldedSchema: string | undefined,
  allowed: Set<string> | undefined,
): boolean {
  if (allowed === undefined) return true;
  // Schemaless routines pass through — they're implicitly in the current
  // context (SQLite built-ins, MySQL user-defined with no db prefix, etc.).
  if (foldedSchema === undefined) return true;
  return allowed.has(foldedSchema);
}

function foldForMatch(value: string, engine: string): string {
  return engine === 'oracle' ? value.toUpperCase() : value.toLowerCase();
}

// Sort: current-schema (or schemaless) bucket first, then alphabetical by
// (foldedSchema, foldedName). Stable across ties thanks to Array.sort.
function compareRoutines(
  a: Routine,
  b: Routine,
  currentFolded: string | undefined,
): number {
  const as = a.signature.qualified.foldedSchema;
  const bs = b.signature.qualified.foldedSchema;

  const aCurrent = as === undefined || as === currentFolded;
  const bCurrent = bs === undefined || bs === currentFolded;
  if (aCurrent && !bCurrent) return -1;
  if (!aCurrent && bCurrent) return 1;

  const asKey = as ?? '';
  const bsKey = bs ?? '';
  if (asKey < bsKey) return -1;
  if (asKey > bsKey) return 1;

  const an = a.signature.qualified.foldedName;
  const bn = b.signature.qualified.foldedName;
  if (an < bn) return -1;
  if (an > bn) return 1;
  return 0;
}
